/**
 * useAnalytics Hook
 *
 * React hook for accessing analytics functionality in components.
 * Provides convenient access to the analytics service methods.
 */

import { useCallback } from 'react';

import analytics from '../analyticsService';
import {
  AnalyticsFeature,
  AnalyticsMode,
  AnalyticsParams,
  LoginType,
  LogoutCause,
  UserProperties,
} from '../types';

export interface UseAnalyticsReturn {
  // Core methods
  logEvent: (eventName: string, params?: AnalyticsParams) => void;
  logScreenView: (screenName: string, screenClass?: string, params?: AnalyticsParams) => void;
  setUserId: (userId: string | null) => void;
  setUserProperties: (properties: UserProperties) => void;
  setAnalyticsMode: (mode: AnalyticsMode) => Promise<void>;
  getAnalyticsMode: () => AnalyticsMode;

  // Authentication events
  logLogin: (loginType?: LoginType, region?: string, service?: string) => void;
  logLogout: (cause?: LogoutCause) => void;
  logForgotPassword: () => void;
  logTwoFactorAuth: (success: boolean) => void;
  logSessionExpired: () => void;

  // Study events
  logStudyCreated: (centerId?: string) => void;
  logStudyViewed: (studyId: string) => void;
  logStudyUpdated: (studyId: string) => void;
  logStudyDeleted: (studyId: string) => void;
  logStudySettingsUpdated: (studyId: string) => void;
  logReportGenerated: (reportType: string, studyId?: string) => void;
  logReportDownloaded: (reportType: string, studyId?: string) => void;
  logReportPrinted: (reportType: string, studyId?: string) => void;
  logReportGenerationFailed: (reportType: string, errorMessage?: string) => void;

  // Center events
  logCenterCreated: () => void;
  logCenterViewed: (centerId: string) => void;
  logCenterUpdated: (centerId: string) => void;
  logCenterSettingsUpdated: (centerId: string) => void;
  logCenterUserAdded: (centerId: string, userRole: string) => void;
  logCenterUserRemoved: (centerId: string) => void;
  logCenterUserRoleUpdated: (centerId: string, userRole: string) => void;
  logCenterSwitched: (centerId: string) => void;
  logSnippetCreated: (centerId: string) => void;
  logSnippetUpdated: (centerId: string) => void;
  logSnippetDeleted: (centerId: string) => void;

  // Profile events
  logProfileViewed: () => void;
  logProfileUpdated: () => void;
  logPasswordChanged: () => void;
  logSecuritySettingsUpdated: () => void;

  // Data viewer events
  logDataViewerAccessed: (studyId: string) => void;
  logNightViewed: (studyId: string, nightNumber: number) => void;
  logAllNightsViewed: (studyId: string) => void;
  logInterpretationViewed: (studyId: string) => void;
  logInterpretationSaved: (studyId: string) => void;
  logInterpretationSigned: (studyId: string) => void;
  logInterpretationReset: (studyId: string) => void;

  // Navigation events
  logNavigation: (destination: string) => void;

  // Error events
  logError: (errorType: string, errorMessage?: string, feature?: AnalyticsFeature) => void;

  // API Client events
  logApiClientUsed: (endpoint: string) => void;
}

/**
 * Hook for accessing analytics service methods in React components.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { logEvent, logLogin } = useAnalytics();
 *
 *   const handleLogin = () => {
 *     // ... login logic
 *     logLogin(LoginType.PASSWORD);
 *   };
 *
 *   return <button onClick={handleLogin}>Login</button>;
 * }
 * ```
 */
export function useAnalytics(): UseAnalyticsReturn {
  // Core methods
  const logEvent = useCallback((eventName: string, params?: AnalyticsParams) => {
    analytics.logEvent(eventName, params);
  }, []);

  const logScreenView = useCallback(
    (screenName: string, screenClass?: string, params?: AnalyticsParams) => {
      analytics.logScreenView({
        screenName,
        screenClass,
        params,
      });
    },
    []
  );

  const setUserId = useCallback((userId: string | null) => {
    analytics.setUserId(userId);
  }, []);

  const setUserProperties = useCallback((properties: UserProperties) => {
    analytics.setUserProperties(properties);
  }, []);

  const setAnalyticsMode = useCallback((mode: AnalyticsMode) => {
    return analytics.setAnalyticsMode(mode);
  }, []);

  const getAnalyticsMode = useCallback(() => {
    return analytics.getAnalyticsMode();
  }, []);

  // Authentication events
  const logLogin = useCallback((loginType?: LoginType, region?: string, service?: string) => {
    analytics.logLogin(loginType, region, service);
  }, []);

  const logLogout = useCallback((cause?: LogoutCause) => {
    analytics.logLogout(cause);
  }, []);

  const logForgotPassword = useCallback(() => {
    analytics.logForgotPassword();
  }, []);

  const logTwoFactorAuth = useCallback((success: boolean) => {
    analytics.logTwoFactorAuth(success);
  }, []);

  const logSessionExpired = useCallback(() => {
    analytics.logSessionExpired();
  }, []);

  // Study events
  const logStudyCreated = useCallback((centerId?: string) => {
    analytics.logStudyCreated(centerId);
  }, []);

  const logStudyViewed = useCallback((studyId: string) => {
    analytics.logStudyViewed(studyId);
  }, []);

  const logStudyUpdated = useCallback((studyId: string) => {
    analytics.logStudyUpdated(studyId);
  }, []);

  const logStudyDeleted = useCallback((studyId: string) => {
    analytics.logStudyDeleted(studyId);
  }, []);

  const logStudySettingsUpdated = useCallback((studyId: string) => {
    analytics.logStudySettingsUpdated(studyId);
  }, []);

  const logReportGenerated = useCallback((reportType: string, studyId?: string) => {
    analytics.logReportGenerated(reportType, studyId);
  }, []);

  const logReportDownloaded = useCallback((reportType: string, studyId?: string) => {
    analytics.logReportDownloaded(reportType, studyId);
  }, []);

  const logReportPrinted = useCallback((reportType: string, studyId?: string) => {
    analytics.logReportPrinted(reportType, studyId);
  }, []);

  const logReportGenerationFailed = useCallback((reportType: string, errorMessage?: string) => {
    analytics.logReportGenerationFailed(reportType, errorMessage);
  }, []);

  // Center events
  const logCenterCreated = useCallback(() => {
    analytics.logCenterCreated();
  }, []);

  const logCenterViewed = useCallback((centerId: string) => {
    analytics.logCenterViewed(centerId);
  }, []);

  const logCenterUpdated = useCallback((centerId: string) => {
    analytics.logCenterUpdated(centerId);
  }, []);

  const logCenterSettingsUpdated = useCallback((centerId: string) => {
    analytics.logCenterSettingsUpdated(centerId);
  }, []);

  const logCenterUserAdded = useCallback((centerId: string, userRole: string) => {
    analytics.logCenterUserAdded(centerId, userRole);
  }, []);

  const logCenterUserRemoved = useCallback((centerId: string) => {
    analytics.logCenterUserRemoved(centerId);
  }, []);

  const logCenterUserRoleUpdated = useCallback((centerId: string, userRole: string) => {
    analytics.logCenterUserRoleUpdated(centerId, userRole);
  }, []);

  const logCenterSwitched = useCallback((centerId: string) => {
    analytics.logCenterSwitched(centerId);
  }, []);

  const logSnippetCreated = useCallback((centerId: string) => {
    analytics.logSnippetCreated(centerId);
  }, []);

  const logSnippetUpdated = useCallback((centerId: string) => {
    analytics.logSnippetUpdated(centerId);
  }, []);

  const logSnippetDeleted = useCallback((centerId: string) => {
    analytics.logSnippetDeleted(centerId);
  }, []);

  // Profile events
  const logProfileViewed = useCallback(() => {
    analytics.logProfileViewed();
  }, []);

  const logProfileUpdated = useCallback(() => {
    analytics.logProfileUpdated();
  }, []);

  const logPasswordChanged = useCallback(() => {
    analytics.logPasswordChanged();
  }, []);

  const logSecuritySettingsUpdated = useCallback(() => {
    analytics.logSecuritySettingsUpdated();
  }, []);

  // Data viewer events
  const logDataViewerAccessed = useCallback((studyId: string) => {
    analytics.logDataViewerAccessed(studyId);
  }, []);

  const logNightViewed = useCallback((studyId: string, nightNumber: number) => {
    analytics.logNightViewed(studyId, nightNumber);
  }, []);

  const logAllNightsViewed = useCallback((studyId: string) => {
    analytics.logAllNightsViewed(studyId);
  }, []);

  const logInterpretationViewed = useCallback((studyId: string) => {
    analytics.logInterpretationViewed(studyId);
  }, []);

  const logInterpretationSaved = useCallback((studyId: string) => {
    analytics.logInterpretationSaved(studyId);
  }, []);

  const logInterpretationSigned = useCallback((studyId: string) => {
    analytics.logInterpretationSigned(studyId);
  }, []);

  const logInterpretationReset = useCallback((studyId: string) => {
    analytics.logInterpretationReset(studyId);
  }, []);

  // Navigation events
  const logNavigation = useCallback((destination: string) => {
    analytics.logNavigation(destination);
  }, []);

  // Error events
  const logError = useCallback(
    (errorType: string, errorMessage?: string, feature?: AnalyticsFeature) => {
      analytics.logError(errorType, errorMessage, feature);
    },
    []
  );

  // API Client events
  const logApiClientUsed = useCallback((endpoint: string) => {
    analytics.logApiClientUsed(endpoint);
  }, []);

  return {
    // Core methods
    logEvent,
    logScreenView,
    setUserId,
    setUserProperties,
    setAnalyticsMode,
    getAnalyticsMode,

    // Authentication events
    logLogin,
    logLogout,
    logForgotPassword,
    logTwoFactorAuth,
    logSessionExpired,

    // Study events
    logStudyCreated,
    logStudyViewed,
    logStudyUpdated,
    logStudyDeleted,
    logStudySettingsUpdated,
    logReportGenerated,
    logReportDownloaded,
    logReportPrinted,
    logReportGenerationFailed,

    // Center events
    logCenterCreated,
    logCenterViewed,
    logCenterUpdated,
    logCenterSettingsUpdated,
    logCenterUserAdded,
    logCenterUserRemoved,
    logCenterUserRoleUpdated,
    logCenterSwitched,
    logSnippetCreated,
    logSnippetUpdated,
    logSnippetDeleted,

    // Profile events
    logProfileViewed,
    logProfileUpdated,
    logPasswordChanged,
    logSecuritySettingsUpdated,

    // Data viewer events
    logDataViewerAccessed,
    logNightViewed,
    logAllNightsViewed,
    logInterpretationViewed,
    logInterpretationSaved,
    logInterpretationSigned,
    logInterpretationReset,

    // Navigation events
    logNavigation,

    // Error events
    logError,

    // API Client events
    logApiClientUsed,
  };
}

export default useAnalytics;
